import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthenticationResult, AxiosRestApplicationClient } from 'utils/restApplicationClient';
import { StorageKeyEnum } from '../enums/StorageKeyEnum';
import { getItem, setItems } from './handlers/storageHandler';

const { tokenStorageKey, refreshTokenStorageKey, tokenTtlStorageKey } = StorageKeyEnum;

/**
 * Query parameter parser
 *
 * @param parameters - query parameters object
 */
const stringifyParameters = (parameters: Record<string, unknown>): string => {
  const keys = Object.keys(parameters);
  return keys
    .map((key: keyof typeof parameters) => {
      const value = parameters[key];
      if (Array.isArray(value)) {
        return `${key}=${value.join(',')}`;
      }
      return `${key}=${value}`;
    })
    .join('&');
};

/**
 * request interceptor
 *
 * @param config - axios default configuration
 */
const requestInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig => {
  let headers: AxiosRequestConfig['headers'];
  const accessToken = getItem(tokenStorageKey);
  const refreshToken = getItem(refreshTokenStorageKey);

  if (config.url && config.url.includes('refreshToken')) {
    headers = {
      Authorization: `${accessToken}`,
      'X-Authorization': `${refreshToken}`,
    };
  } else {
    headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  config.paramsSerializer = { serialize: (parameters) => stringifyParameters(parameters as Record<string, unknown>) };

  config.headers = headers;
  return config;
};

/**
 * response interceptor
 *
 * @param response - axios response
 */
const responseInterceptor = (response: AxiosResponse<unknown>): AxiosResponse<unknown> => {
  if (response.config.url === 'api/auth/user' || response.config.url === 'api/auth/refreshToken') {
    const { accessToken, refreshToken, ttl } = response.data as AuthenticationResult;

    setItems({
      [tokenStorageKey]: accessToken,
      [refreshTokenStorageKey]: refreshToken,
      [tokenTtlStorageKey]: String(ttl),
    });
  }

  return response;
};

const axiosInstance = Axios.create();

axiosInstance.interceptors.request.use(requestInterceptor);
axiosInstance.interceptors.response.use(responseInterceptor);

const api: AxiosRestApplicationClient = new AxiosRestApplicationClient('/', axiosInstance);

export { api };
export default axiosInstance;
